<template>
    <section class="relative py-16 sm:py-20 lg:py-28 overflow-hidden">

        <!-- Background -->
        <div class="absolute inset-0 pointer-events-none">
            <div class="absolute top-1/3 right-0 w-96 h-96 rounded-full opacity-10"
                 style="background: radial-gradient(circle, #a75ded 0%, transparent 70%); filter: blur(90px);"></div>
            <div class="absolute bottom-1/3 left-0 w-80 h-80 rounded-full opacity-10"
                 style="background: radial-gradient(circle, #e879f9 0%, transparent 70%); filter: blur(90px);"></div>
        </div>

        <!-- Top divider -->
        <div class="absolute top-0 left-0 right-0 h-px"
             style="background: linear-gradient(90deg, transparent 0%, rgba(167,93,237,0.4) 30%, rgba(232,121,249,0.5) 50%, rgba(167,93,237,0.4) 70%, transparent 100%);"></div>

        <div class="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">

            <!-- Header -->
            <div class="text-center mb-12">
                <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold tracking-[0.15em] uppercase mb-6 scroll-reveal"
                     style="border-color: rgba(167,93,237,0.4); background: rgba(167,93,237,0.08); color: #c084fc;">
                    <span class="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style="background:#a75ded;"></span>
                    {{ t('faqLabel') }}
                </div>

                <h2 class="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4 scroll-reveal">
                    <span class="text-white block">{{ t('faqTitle') }}</span>
                    <span class="block mt-1"
                          style="background:linear-gradient(135deg,#7c3aed,#a75ded,#e879f9);-webkit-background-clip:text;background-clip:text;color:transparent;">
                        Expert Insights
                    </span>
                </h2>

                <p class="text-white/60 text-lg sm:text-xl max-w-2xl mx-auto scroll-reveal">
                    {{ t('faqDescription') }}
                </p>
            </div>

            <!-- FAQ Items -->
            <div class="space-y-3">
                <div v-for="(faq, idx) in faqs" :key="idx"
                     class="group rounded-2xl border overflow-hidden transition-all duration-400 scroll-reveal"
                     :style="{
                         background: expandedItems[idx] ? 'rgba(167,93,237,0.08)' : 'rgba(10,4,22,0.85)',
                         borderColor: expandedItems[idx] ? 'rgba(167,93,237,0.4)' : 'rgba(167,93,237,0.14)',
                         boxShadow: expandedItems[idx] ? '0 8px 32px rgba(167,93,237,0.15)' : 'none',
                     }">

                    <!-- Question -->
                    <button class="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 text-left transition-colors duration-300"
                            :class="expandedItems[idx] ? '' : 'hover:bg-white/[0.02]'"
                            @click="toggleItem(idx)">
                        <div class="flex items-center gap-4">
                            <span class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black"
                                  :style="{ background: expandedItems[idx] ? 'linear-gradient(135deg,#7c3aed,#a75ded)' : 'rgba(167,93,237,0.15)', color: expandedItems[idx] ? '#fff' : '#a75ded' }">
                                {{ idx + 1 }}
                            </span>
                            <span class="text-white font-bold text-base sm:text-lg leading-snug">{{ faq.question }}</span>
                        </div>
                        <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-400"
                             :style="{ background: expandedItems[idx] ? 'linear-gradient(135deg,#7c3aed,#a75ded)' : 'rgba(167,93,237,0.1)', transform: expandedItems[idx] ? 'rotate(180deg)' : 'rotate(0)' }">
                            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
                            </svg>
                        </div>
                    </button>

                    <!-- Answer -->
                    <div class="overflow-hidden transition-all duration-500"
                         :style="{ maxHeight: expandedItems[idx] ? '400px' : '0px' }">
                        <div class="px-6 sm:px-8 pb-6 pt-1">
                            <div class="w-full h-px mb-4" style="background:linear-gradient(90deg,rgba(167,93,237,0.3),transparent);"></div>
                            <p class="text-white/65 leading-relaxed pl-11">{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Still have questions -->
            <div class="mt-12 text-center p-8 rounded-3xl border scroll-reveal"
                 style="background:rgba(10,4,22,0.85);border-color:rgba(167,93,237,0.2);">
                <div class="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
                     style="background:linear-gradient(135deg,#7c3aed,#a75ded);box-shadow:0 8px 24px rgba(167,93,237,0.4);">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                </div>
                <h3 class="text-white font-black text-xl mb-2">Still have questions?</h3>
                <p class="text-white/50 text-sm mb-5">Our support team is available 24/7 to help you.</p>
                <a href="#" class="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 hover:scale-[1.04]"
                   style="background:linear-gradient(135deg,#7c3aed,#a75ded);color:#fff;box-shadow:0 6px 20px rgba(167,93,237,0.4);">
                    Contact Support
                </a>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useTranslation } from '@/composables/useTranslation'

const { t } = useTranslation()
const expandedItems = ref({})

const faqs = [
    {
        question: 'How does AI-driven yield optimization function?',
        answer: 'Our proprietary AI infrastructure continuously analyzes blockchain activity, liquidity conditions, and market behavior in real time to optimize portfolio allocation across multiple decentralized strategies. The system dynamically adapts to evolving market environments using predictive analytics, historical intelligence, and automated risk-management frameworks.'
    },
    {
        question: 'Is my digital capital secure and non-custodial?',
        answer: 'Yes. DeFiSharing￼ operates through non-custodial blockchain infrastructure, allowing users to retain direct control of their digital assets at all times. All transactions are transparently recorded on-chain, independently verifiable, and designed to minimize centralized counterparty risk.'
    },
    {
        question: 'What are the withdrawal terms and timelines?',
        answer: 'Withdrawals process between 0-24 hours depending on your plan. Professional plan members enjoy priority processing. Emergency withdrawals with a small fee are available within 4 hours.'
    },
    {
        question: 'Can I earn network rewards?',
        answer: 'Absolutely! Invite other investors through your referral link and earn rewards based on their deposits. The more you contribute to the ecosystem, the more rewards you unlock.'
    },
    {
        question: 'Which blockchain networks does DeFiSharing￼ support?',
        answer: 'Our infrastructure currently supports Ethereum, BNB Chain, Polygon, Base, and TRON, with continuous expansion into additional blockchain ecosystems. Users can efficiently transfer and manage digital assets through integrated cross-chain connectivity and decentralized liquidity routing.'
    },
    {
        question: 'Are there any undisclosed platform fees?',
        answer: 'No. DeFiSharing￼ maintains a transparent fee structure with clearly defined performance-based charges applied only to qualifying platform activity. Blockchain transaction costs remain visible, minimal, and fully verifiable on-chain. Refer to our pricing and protocol documentation for complete transparency.'
    },
]

const toggleItem = (idx) => { expandedItems.value[idx] = !expandedItems.value[idx] }

onMounted(() => {
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
            entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in-view') })
        }, { threshold: 0.1 })
        document.querySelectorAll('.scroll-reveal').forEach(el => io.observe(el))
    } else {
        document.querySelectorAll('.scroll-reveal').forEach(el => el.classList.add('in-view'))
    }
})
</script>

<style scoped>
.scroll-reveal { opacity:0; transform:translateY(28px); transition:opacity 0.7s ease,transform 0.7s ease; }
.scroll-reveal.in-view { opacity:1; transform:none; }
@media(prefers-reduced-motion:reduce){ .scroll-reveal{ opacity:1;transform:none;transition:none; } }
</style>
